/* eslint-disable max-lines */
import { useWatch } from "react-hook-form";
import { useMemo, useCallback } from "react";
import { useCompositionsList, useCurrentComposition } from "@design-lab/entities/composition";
import { useCombinedProductData } from "@design-lab/entities/product";
import type { printOnDemand, product, ID } from "common/utils/models";
import { useCartQuantity } from "@design-lab/features/manage-cart";
import { useEditMode } from "@design-lab/shared/hooks/useEditMode";
import { useArtworkForm, useDtfPricing } from "@design-lab/entities/artwork";

const DEFAULT_TIERS_DETAILS = {
  shirtsForNextTier: 0,
  savingsForNextTier: 0,
  totalTiers: 0,
  tiersMet: 0,
  pricing: []
};

const FIXED_QUANTITY_TIERS = [1, 10, 25, 50, 100, 500];

// eslint-disable-next-line max-lines-per-function
export function useCompositionPricing() {
  const { getCompositionById, formCompositions } = useCompositionsList();
  const { priceData } = useCombinedProductData();
  const { isEditMode, assemblyGroupId } = useEditMode();
  const { data: cartQuantity } = useCartQuantity(isEditMode ? assemblyGroupId : undefined);
  const { form: artworkForm } = useArtworkForm();
  const { data: pricingDataPrintPerfect } = useDtfPricing();
  const { data: pricingDataCustom } = useDtfPricing("custom");
  const { currentComposition } = useCurrentComposition();

  const enhancementPriceMap = useMemo(
    () => ({
      print_original: 0,
      full_enhancements: parseFloat(pricingDataPrintPerfect?.enhancementTierPrice || "0"),
      expert_edit_addon: parseFloat(pricingDataPrintPerfect?.expertEditAddonTierPrice || "0"),
      custom: parseFloat(pricingDataCustom?.enhancementTierPrice || "0")
    }),
    [
      pricingDataCustom?.enhancementTierPrice,
      pricingDataPrintPerfect?.enhancementTierPrice,
      pricingDataPrintPerfect?.expertEditAddonTierPrice
    ]
  );

  const artworkInfoForm = useWatch({
    control: artworkForm.control,
    name: `info`
  });

  const artworkPlacements = useWatch({
    control: artworkForm.control
  });

  const frontExpertEditAddon = useWatch({
    control: artworkForm.control,
    name: `info.front.jiffyDesignServices`
  });

  const backExpertEditAddon = useWatch({
    control: artworkForm.control,
    name: `info.back.jiffyDesignServices`
  });

  const { front, back } = artworkInfoForm || {};

  const frontEnhancementTier = frontExpertEditAddon ? "expert_edit_addon" : front?.enhancementTier;
  const backEnhancementTier = backExpertEditAddon ? "expert_edit_addon" : back?.enhancementTier;

  const frontEnhancementPrice =
    enhancementPriceMap[frontEnhancementTier] ?? enhancementPriceMap["print_original"];
  const backEnhancementPrice =
    enhancementPriceMap[backEnhancementTier] ?? enhancementPriceMap["print_original"];

  const cartTotal = parseFloat(cartQuantity?.cartTotal ?? "0");

  // Calculate quality total based on selected artworks
  let qualityTotal = 0;

  if (artworkPlacements?.front?.artworkId || artworkPlacements?.frontLeftChest?.artworkId) {
    qualityTotal += frontEnhancementPrice;
  }
  if (artworkPlacements?.back?.artworkId) {
    qualityTotal += backEnhancementPrice;
  }

  // Get base price for a variant
  const getBasePrice = useCallback(
    (variantId: string): number => {
      if (
        !priceData ||
        !priceData.variantPrices[variantId] ||
        !priceData.variantPrices[variantId]["0"]
      ) {
        return 0;
      }
      return parseFloat(priceData.variantPrices[variantId]["0"]);
    },
    [priceData]
  );

  // Calculate total base value for price tier determination (excludes cart total for frontend calculation)
  const totalBaseValue = useMemo(() => {
    if (!priceData || !formCompositions) return 0;

    // Calculate total from all added compositions
    const allCompositionsTotal = formCompositions.reduce((total, formComposition) => {
      // Iterate through variants that have quantities set
      const compositionTotal = Object.entries(formComposition.variants).reduce(
        (compTotal, [variantId, quantity]) => {
          const qty = typeof quantity === "number" ? quantity : 0;
          if (qty === 0) return compTotal;

          const basePrice = getBasePrice(variantId);
          return compTotal + basePrice * qty;
        },
        0
      );

      return total + compositionTotal;
    }, 0);

    // Return total without cart price, but including quality enhancements
    return allCompositionsTotal + qualityTotal;
  }, [priceData, formCompositions, qualityTotal, getBasePrice]);

  // Calculate total base value including cart (for price tier determination)
  const totalBaseValueWithCart = useMemo(
    () => totalBaseValue + cartTotal,
    [totalBaseValue, cartTotal]
  );

  // Determine price tier based on total value
  const determinePriceTier = (
    totalValue: number,
    composition: printOnDemand.getProduct.Composition,
    priceDataParam: product.cp.Response
  ): string => {
    const sampleVariantId = composition.variants[0]?.id;
    if (!sampleVariantId || !priceDataParam.variantPrices[sampleVariantId]) return "0";

    const priceTiers = Object.keys(priceDataParam.variantPrices[sampleVariantId])
      .map(Number)
      .sort((a, b) => b - a); // Sort descending for easier logic

    return priceTiers.find(tier => totalValue >= tier)?.toString() ?? "0";
  };

  // Get variant price for a specific composition and variant
  const getVariantPrice = useCallback(
    (compositionId: ID, variantId: string): string | undefined => {
      const composition = getCompositionById(compositionId);
      if (!composition || !priceData) return undefined;

      // Calculate price tier based on all compositions
      const currentPriceTier = determinePriceTier(totalBaseValueWithCart, composition, priceData);

      // Get price for the variant at the current tier
      if (!priceData.variantPrices[variantId]) return undefined;
      const price = priceData.variantPrices[variantId][currentPriceTier];
      return price || undefined;
    },
    [getCompositionById, priceData, totalBaseValueWithCart]
  );

  const getCompositionPrice = useCallback(
    (compositionId: ID): number => {
      if (!priceData || !formCompositions) return 0;

      const formComposition = formCompositions.find(comp => comp.id === compositionId);
      if (!formComposition) return 0;

      return Object.entries(formComposition.variants).reduce((compTotal, [variantId, quantity]) => {
        const qty = typeof quantity === "number" ? quantity : 0;
        if (qty === 0) return compTotal;

        const variantPrice = getVariantPrice(compositionId, variantId);
        if (!variantPrice) return compTotal;

        return compTotal + parseFloat(variantPrice) * qty;
      }, 0);
    },
    [formCompositions, getVariantPrice, priceData]
  );

  // Calculate total price with correct price tier applied
  const totalPriceWithBulkDiscount = useMemo(() => {
    if (!priceData || !formCompositions || formCompositions.length === 0) return 0;

    // Calculate total using getVariantPrice for each variant
    const compositionsTotal = formCompositions.reduce((total, formComposition) => {
      const compositionTotal = Object.entries(formComposition.variants).reduce(
        (compTotal, [variantId, quantity]) => {
          const qty = typeof quantity === "number" ? quantity : 0;
          if (qty === 0) return compTotal;

          // Use getVariantPrice to get the tier-based price
          const variantPrice = getVariantPrice(formComposition.id, variantId);
          if (!variantPrice) return compTotal;

          return compTotal + parseFloat(variantPrice) * qty;
        },
        0
      );

      return total + compositionTotal;
    }, 0);

    // Return total with tiered pricing plus quality enhancements
    return compositionsTotal + qualityTotal;
  }, [priceData, formCompositions, qualityTotal, getVariantPrice]);

  // Calculate total price for a specific tier
  const totalPriceWithBulkDiscountForTier = useCallback(
    (tier: number): number => {
      if (!priceData || !formCompositions || formCompositions.length === 0) return 0;

      // Calculate total using the specified tier prices
      const compositionsTotal = formCompositions.reduce((total, formComposition) => {
        const compositionTotal = Object.entries(formComposition.variants).reduce(
          (compTotal, [variantId, quantity]) => {
            const qty = typeof quantity === "number" ? quantity : 0;
            if (qty === 0) return compTotal;

            // Get price for the variant at the specified tier
            const variantPrices = priceData.variantPrices[variantId];
            if (!variantPrices) return compTotal;

            const tierPrice = variantPrices[tier.toString()];
            if (!tierPrice) return compTotal;

            return compTotal + parseFloat(tierPrice) * qty;
          },
          0
        );

        return total + compositionTotal;
      }, 0);

      // Return total with specified tier pricing plus quality enhancements
      return compositionsTotal + qualityTotal;
    },
    [priceData, formCompositions, qualityTotal]
  );

  // Calculate savings from bulk discount
  const savings = useMemo(() => {
    // Only calculate savings if there's a valid total
    if (!totalBaseValue || !totalPriceWithBulkDiscount) return 0;

    // Savings is the difference between base price and discounted price
    const savingsAmount = totalBaseValue - totalPriceWithBulkDiscount;

    // Ensure we don't show negative savings
    return Math.max(0, savingsAmount);
  }, [totalBaseValue, totalPriceWithBulkDiscount]);

  // Calculate shirts needed for next tier and potential savings
  const tiersDetails = useMemo(() => {
    if (!priceData || !formCompositions || formCompositions.length === 0) {
      return DEFAULT_TIERS_DETAILS;
    }

    // Get the first composition to determine price tiers
    const firstComposition = formCompositions[0];
    if (!firstComposition) {
      return DEFAULT_TIERS_DETAILS;
    }

    const composition = currentComposition?.id
      ? getCompositionById(currentComposition.id)
      : getCompositionById(firstComposition.id);
    if (!composition) {
      return DEFAULT_TIERS_DETAILS;
    }

    const sampleVariantId = composition.variants[0]?.id;
    if (!sampleVariantId || !priceData.variantPrices[sampleVariantId]) {
      return DEFAULT_TIERS_DETAILS;
    }

    // Get base price of a single shirt (using the first variant as reference)
    const basePrice = getBasePrice(sampleVariantId);

    // Get all price tiers sorted in ascending order
    const priceTiers = Object.keys(priceData.variantPrices[sampleVariantId])
      .map(Number)
      .sort((a, b) => a - b);

    // Calculate pricing tiers breakdown based on fixed quantities
    const pricing = FIXED_QUANTITY_TIERS.map(quantity => {
      // Determine which price tier applies to this quantity
      const applicableTier = determinePriceTier(quantity * basePrice, composition, priceData);
      const price = parseFloat(priceData.variantPrices[sampleVariantId]?.[applicableTier] || "0");

      return {
        quantity,
        price: price || basePrice // Fallback to base price if no tier price found
      };
    });

    // Calculate total tiers and tiers met
    const totalTiers = priceTiers.length - 1;
    const currentTier = determinePriceTier(totalBaseValueWithCart, composition, priceData);
    const currentTierIndex = priceTiers.findIndex(tier => tier.toString() === currentTier);

    // Only count tiers that provide actual savings (exclude base tier)
    // If we're at the base tier (index 0), tiersMet should be 0
    // If we're at tier 1 or higher, tiersMet should be the tier index
    const tiersMet = currentTierIndex > 0 ? currentTierIndex : 0;

    // If we're at the highest tier or no next tier exists
    if (currentTierIndex === -1 || currentTierIndex >= priceTiers.length - 1) {
      return { ...DEFAULT_TIERS_DETAILS, totalTiers, tiersMet, pricing };
    }

    // Get next tier threshold
    const nextTierThreshold = priceTiers[currentTierIndex + 1];
    if (nextTierThreshold === undefined) {
      return { ...DEFAULT_TIERS_DETAILS, totalTiers, tiersMet, pricing };
    }

    // Calculate how much more value is needed to reach next tier
    const additionalValueNeeded = nextTierThreshold - totalBaseValueWithCart;

    // Get base price of a single shirt (using the first variant as reference)
    if (basePrice === 0) {
      return { ...DEFAULT_TIERS_DETAILS, totalTiers, tiersMet, pricing };
    }

    // Calculate potential savings if next tier is reached
    const currentPrice = parseFloat(priceData.variantPrices[sampleVariantId]?.[currentTier] || "0");
    const nextTierPrice = parseFloat(
      priceData.variantPrices[sampleVariantId]?.[nextTierThreshold.toString()] || "0"
    );

    // Calculate how many shirts needed for next tier
    const shirtsNeeded = Math.ceil(additionalValueNeeded / basePrice);

    if (currentPrice === 0 || nextTierPrice === 0) {
      return {
        ...DEFAULT_TIERS_DETAILS,
        shirtsForNextTier: shirtsNeeded,
        totalTiers,
        tiersMet,
        pricing
      };
    }

    const newTier0Total = totalBaseValue + basePrice * shirtsNeeded;
    const newTierNTotal =
      totalPriceWithBulkDiscountForTier(nextTierThreshold) + nextTierPrice * shirtsNeeded;
    const nextTierSavings = newTier0Total - newTierNTotal;
    const potentialSavings = nextTierSavings - savings;

    return {
      shirtsForNextTier: Math.max(0, shirtsNeeded),
      savingsForNextTier: Math.max(0, potentialSavings),
      totalTiers,
      tiersMet,
      pricing
    };
  }, [
    priceData,
    formCompositions,
    totalBaseValueWithCart,
    getCompositionById,
    determinePriceTier,
    getBasePrice,
    savings,
    cartTotal,
    currentComposition,
    totalPriceWithBulkDiscountForTier,
    totalBaseValue
  ]);

  return {
    getVariantPrice,
    getCompositionPrice,
    totalBaseValue,
    totalPriceWithBulkDiscount,
    totalPriceWithBulkDiscountForTier,
    savings,
    qualityTotal,
    priceData,
    ...tiersDetails
  };
}
